<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">
        {{ editing ? "Editar empresa" : "Nueva empresa" }}
      </h1>
    </div>

    <wz-card :title="editing ? 'Datos de la empresa' : 'Datos de la nueva empresa'" :body-padding="false">

      <wz-tabs :items="tabItems" v-model="activeTab" variant="underline" style="padding: 0 20px;">

        <!-- Tab: Datos generales -->
        <template #general>
          <form @submit.prevent="save" novalidate>
            <div class="flex flex-wrap -mx-2">

              <div class="w-full mb-3 px-2">
                <wz-input
                  label="RUC"
                  required
                  v-model.trim="form.ruc"
                  placeholder="Ingresar número de RUC"
                  maxlength="11"
                  :disabled="disabledField"
                  :error="$v.form.ruc.$error
                    ? (!$v.form.ruc.required   ? 'RUC es requerido'
                      : !$v.form.ruc.numeric   ? 'RUC debe tener un valor numérico'
                      : !$v.form.ruc.minLength ? 'RUC debe tener 11 dígitos' : '')
                    : companyIdExists ? 'Ya existe una empresa registrada con el mismo RUC' : ''"
                />
              </div>

              <div class="w-full mb-3 px-2">
                <wz-input
                  label="Razón social"
                  required
                  v-model="form.name"
                  placeholder="Ingresar razón social"
                  maxlength="200"
                  :error="$v.form.name.$error && !$v.form.name.required ? 'Razón social es requerida' : ''"
                />
              </div>

              <div class="w-full mb-3 px-2">
                <wz-input
                  label="Dirección fiscal"
                  required
                  v-model="form.address"
                  placeholder="Ingresar dirección fiscal"
                  :error="$v.form.address.$error && !$v.form.address.required ? 'Dirección fiscal es requerida' : ''"
                />
              </div>

              <div class="w-full mb-3 px-2">
                <wz-input
                  label="Representante legal"
                  v-model="form.legalRepresentative"
                  placeholder="Representante legal"
                />
              </div>

              <!-- CDEF010 §4: datos que toda empresa peruana tiene (columnas propias) -->
              <div class="w-full md:w-1/2 mb-3 px-2">
                <wz-input
                  label="Partida registral de poderes"
                  v-model.trim="form.registryEntry"
                  maxlength="100"
                  placeholder="N° de partida (SUNARP)"
                />
              </div>
              <div class="w-full md:w-1/2 mb-3 px-2">
                <wz-input
                  label="Teléfono"
                  v-model.trim="form.phone"
                  maxlength="30"
                  placeholder="Teléfono"
                />
              </div>
              <div class="w-full mb-3 px-2">
                <wz-select-search
                  label="Ubigeo del domicilio fiscal"
                  v-model="form.ubigeo"
                  :items="districts"
                  defaultOption="Elegir ubigeo"
                  search-placeholder="Buscar departamento, provincia o distrito..."
                />
              </div>

              <!-- Logo -->
              <div class="w-full mb-3 px-2" v-if="editing">
                <div class="wz-form-group">
                  <label class="wz-form-label">Logo de la empresa</label>
                  <div class="flex items-start flex-wrap -mx-2">
                    <div
                      v-if="logoSrc"
                      class="border rounded flex items-center justify-center mr-4 mb-3"
                      style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                    >
                      <img :src="logoSrc" alt="Logo" style="max-width:100%;max-height:100%;object-fit:contain" />
                    </div>
                    <div class="flex-1">
                      <wz-file-input
                        id-file="company-logo-upload"
                        :accept-file="['.png', '.jpg', '.jpeg']"
                        class="mb-1"
                        @onChange="files => onFilePick('logo', files)"
                      />
                      <small class="text-muted">PNG o JPG. Tamaño recomendado: 200×80px. PNG con fondo transparente es ideal.</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Logo: aviso en creación (la subida necesita la empresa ya guardada) -->
              <div class="w-full mb-3 px-2" v-else>
                <div class="wz-form-group">
                  <label class="wz-form-label">Logo de la empresa</label>
                  <small class="text-muted block">Podrás subir el logo de la empresa después de guardarla.</small>
                </div>
              </div>

            </div>

            <div class="flex justify-end gap-2 mt-2 mb-4">
              <wz-button variant="light" @click="$router.push({ name: 'company-list' })">
                {{ editing ? "Volver" : "Cancelar" }}
              </wz-button>
              <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
            </div>
          </form>
        </template>

        <!-- Tab: Campos personalizados (CDEF010) -->
        <template #custom>
          <div class="py-3">
            <custom-fields-section v-if="editing && $route.params.id" entity="EMPRESA" :entity-id="Number($route.params.id)" />
          </div>
        </template>

        <!-- Tab: Firmantes (CDEF011 §5) -->
        <template #signers>
          <div class="py-3">
            <company-signers-section v-if="editing && $route.params.id" :company-id="Number($route.params.id)" />
            <company-email-domains v-if="editing && $route.params.id" :company-id="Number($route.params.id)" class="mt-3" />
            <company-delivery-channels v-if="editing && $route.params.id" :company-id="Number($route.params.id)" class="mt-3" />
          </div>
        </template>

        <!-- Tab: Firma y sello -->
        <template #signature>
          <form @submit.prevent="saveConfig" novalidate>
            <div class="flex flex-wrap -mx-2">

              <!-- Firma -->
              <div class="w-full mb-3 px-2">
                <div class="wz-form-group">
                  <label class="wz-form-label">Firma del representante legal</label>
                  <div class="flex items-start flex-wrap -mx-2">
                    <div
                      v-if="signatureSrc"
                      class="border rounded flex items-center justify-center mr-4 mb-3"
                      style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                    >
                      <img :src="signatureSrc" alt="Firma" style="max-width:100%;max-height:100%;object-fit:contain" />
                    </div>
                    <div class="flex-1">
                      <wz-file-input
                        id-file="company-signature-upload"
                        :accept-file="['.png', '.jpg', '.jpeg']"
                        class="mb-1"
                        @onChange="files => onFilePick('signature', files)"
                      />
                      <small class="text-muted">Escanee la firma en hoja blanca. PNG con fondo transparente para mejor resultado.</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="w-full mb-3 px-2">
                <wz-input
                  label="Cargo del representante"
                  v-model="configForm.representativeTitle"
                  placeholder="Ej: Gerente General"
                />
              </div>

              <!-- Sello -->
              <div class="w-full mb-3 px-2">
                <div class="wz-form-group">
                  <label class="wz-form-label">Sello de la empresa</label>
                  <div class="flex items-start flex-wrap -mx-2">
                    <div
                      v-if="sealSrc"
                      class="border rounded flex items-center justify-center mr-4 mb-3"
                      style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                    >
                      <img :src="sealSrc" alt="Sello" style="max-width:100%;max-height:100%;object-fit:contain" />
                    </div>
                    <div class="flex-1">
                      <wz-file-input
                        id-file="company-seal-upload"
                        :accept-file="['.png', '.jpg', '.jpeg']"
                        class="mb-1"
                        @onChange="files => onFilePick('seal', files)"
                      />
                      <small class="text-muted">Opcional. PNG con fondo transparente.</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="w-full mb-3 px-2">
                <wz-textarea
                  label="Texto legal pie de boleta"
                  v-model="configForm.payslipFooterText"
                  placeholder="Boleta de pago emitida al amparo del D.S. 001-98-TR y D.Leg. 1310..."
                  :rows="3"
                />
              </div>

            </div>

            <div class="flex justify-end gap-2 mt-2 mb-4">
              <wz-button variant="light" @click="$router.push({ name: 'company-list' })">Volver</wz-button>
              <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
            </div>
          </form>
        </template>

      </wz-tabs>

    </wz-card>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { CompanyService, UbigeoService } from '@/core/services'
import CustomFieldsSection from '@/core/components/CustomFieldsSection.vue'
import CompanySignersSection from '../Components/CompanySignersSection.vue'
import CompanyDeliveryChannels from '../Components/CompanyDeliveryChannels.vue'
import CompanyEmailDomains from '../Components/CompanyEmailDomains.vue'
import { numeric, required, minLength } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

// BYPASS TEMPORAL QA (2026-08-05): la consulta a la API de SUNAT por RUC está devolviendo error y
// bloquea el alta de empresa. Con el flag en false se saltea la consulta y los campos (nombre,
// dirección) se completan a mano; el guardado no queda bloqueado. Reactivar poniéndolo en true cuando
// la API externa esté operativa. NO borrar la integración (queryRuc sigue intacto).
const SUNAT_LOOKUP_ENABLED = false

export default {
  components: { CustomFieldsSection, CompanySignersSection, CompanyDeliveryChannels, CompanyEmailDomains },
  data() {
    return {
      loading:  true,
      editing:  false,
      activeTab: 'general',

      form: {
        ruc:                 null,
        name:                null,
        address:             null,
        legalRepresentative: null,
        registryEntry:       null,
        phone:               null,
        ubigeo:              null,
      },
      districts: [],
      companyIdExists: false,

      configForm: {
        representativeTitle: null,
        payslipFooterText:   null,
      },

      files:    { logo: null, signature: null, seal: null },
      previews: { logo: null, signature: null, seal: null },
      serverUrls: { logo: null, signature: null, seal: null },
    }
  },

  validations: {
    form: {
      ruc:     { required, numeric, minLength: minLength(11) },
      name:    { required },
      address: { required },
    },
  },

  computed: {
    ...mapGetters({ user: 'auth/getUser' }),
    tabItems() {
      return [
        { key: 'general',   label: 'Datos generales', icon: 'ri-building-line' },
        { key: 'signature', label: this.editing ? 'Firma y sello' : 'Firma y sello (guarda primero)', icon: 'ri-quill-pen-line', disabled: !this.editing },
        { key: 'custom',    label: this.editing ? 'Campos personalizados' : 'Campos personalizados (guarda primero)', icon: 'ri-list-settings-line', disabled: !this.editing },
        { key: 'signers',   label: this.editing ? 'Firmantes' : 'Firmantes (guarda primero)', icon: 'ri-user-star-line', disabled: !this.editing },
      ]
    },
    disabledField() {
      return !!this.$route.params.id
    },
    logoSrc()      { return this.previews.logo      || this.serverUrls.logo      || null },
    signatureSrc() { return this.previews.signature || this.serverUrls.signature || null },
    sealSrc()      { return this.previews.seal      || this.serverUrls.seal      || null },
  },

  watch: {
    '$route.params.id': function () {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.ruc': function (val) {
      if (val && val.length === 11 && !this.editing) {
        this.getQueryRuc()
      }
    },
  },

  methods: {
    onFilePick(type, files) {
      if (!files || files.length === 0) return
      const file = files[0]
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 2MB')
        return
      }
      this.files[type]    = file
      this.previews[type] = URL.createObjectURL(file)
    },

    async loadImages(id, payload) {
      const pathMap = { logo: payload.logoPath, signature: payload.signaturePath, seal: payload.sealPath }
      await Promise.all(
        ['logo', 'signature', 'seal']
          .filter(t => pathMap[t])
          .map(async t => {
            try {
              const blob = await CompanyService.getConfigImage(id, t)
              this.serverUrls[t] = URL.createObjectURL(blob)
            } catch (e) { /* imagen no encontrada */ }
          })
      )
    },

    async getQueryRuc() {
      // BYPASS TEMPORAL QA (2026-08-05): ver SUNAT_LOOKUP_ENABLED arriba. Saltea la consulta a SUNAT y
      // libera el alta manual (companyIdExists=false para no bloquear el guardado).
      if (!SUNAT_LOOKUP_ENABLED) { this.companyIdExists = false; return }
      try {
        this.companyIdExists = false
        const resp = (await CompanyService.queryRuc(this.form.ruc)).payload
        this.companyIdExists = resp.alreadyExists
        if (!resp.alreadyExists) {
          this.form.name    = resp.name
          this.form.address = resp.address
        } else {
          this.form.name    = null
          this.form.address = null
        }
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      }
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error || this.companyIdExists) {
        this.$nextTick(() => {
          const el = document.querySelector('.wz-form-error-msg')
          if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
        })
        return
      }

      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.form }
        let response
        if (!this.editing) response = await CompanyService.save(data)
        else               response = await CompanyService.update(this.$route.params.id, data)

        if (this.files.logo) {
          const companyId = this.editing ? this.$route.params.id : response.payload.id
          const formData  = new FormData()
          formData.append('logo', this.files.logo)
          await CompanyService.saveConfig(companyId, formData)
          this.files.logo = null
        }

        Alert.success('Se almacenó la información correctamente')
        if (!this.editing) {
          this.$router.push({ name: 'company-edit', params: { id: response.payload.id } })
          this.editing = true
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async saveConfig() {
      try {
        this.$store.dispatch('app/loading', true)
        const formData = new FormData()
        if (this.configForm.representativeTitle != null) formData.append('representativeTitle', this.configForm.representativeTitle)
        if (this.configForm.payslipFooterText   != null) formData.append('payslipFooterText',   this.configForm.payslipFooterText)
        if (this.files.signature) formData.append('signature', this.files.signature)
        if (this.files.seal)      formData.append('seal',      this.files.seal)

        await CompanyService.saveConfig(this.$route.params.id, formData)
        Alert.success('Configuración guardada correctamente')
        this.files.signature = null
        this.files.seal      = null
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar la configuración')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await CompanyService.get(id)).payload
        this.form = {
          ruc:                 payload.ruc,
          name:                payload.name,
          address:             payload.address,
          legalRepresentative: payload.legalRepresentative,
          registryEntry:       payload.registryEntry || null,
          phone:               payload.phone || null,
          ubigeo:              payload.ubigeo || null,
        }
        this.configForm.representativeTitle = payload.representativeTitle || null
        this.configForm.payslipFooterText   = payload.payslipFooterText   || null
        this.editing = true
        await this.loadImages(id, payload)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'company-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id)
    else this.loading = false
    try {
      const districts = await UbigeoService.listDistricts()
      this.districts = (districts.payload || []).map(d => ({ key: d.id, label: d.description }))
    } catch (ex) {
      console.error(ex)
    }
  },
}
</script>

<style lang="stylus" scoped></style>
